import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Award, BadgeCheck, BadgeIndianRupee, Banknote, Bell, Building, Building2, ChevronDown, ChevronRight, Clock,
  CreditCard,
  FileSearch,
  FileText, FlaskConical, Gauge, Gavel, Globe, HandCoins, HandHeart, HeartHandshake, KanbanSquare,
  LayoutGrid, LifeBuoy, LogOut, Mail, Megaphone, Menu, MessageSquareWarning, Moon, PieChart, Radar,
  Scale,
  Receipt,
  ScrollText, Search,
  ShieldAlert, ShieldCheck, Sparkles, Stamp, Sun, Target, Truck, UserPlus, UserSquare2, Users,
  Wallet, X, BookLock, CalendarDays, GraduationCap, UtensilsCrossed, Vote, Users2,
} from "lucide-react";

import { Switch } from "@/components/ui/switch";
import { NotificationBell } from "@/components/layout/NotificationBell";
import { GuidedTour } from "@/components/common/GuidedTour";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { setLanguage } from "@/i18n";
import { api } from "@/lib/api";
import { startDomTranslation, stopDomTranslation, setDataTranslation } from "@/domTranslate";

const NAV = [
  { to: "/dashboard/executive", label: "Overview", icon: Gauge, testId: "nav-executive" },
  { to: "/dashboard/impact", label: "Whole-charity View", icon: LayoutGrid, testId: "nav-impact",
    hidden: true },
  { to: "/charity/donors", label: "Donors & CRM", icon: Users, testId: "nav-donors" },  { to: "/charity/donations", label: "Donations", icon: HandCoins, testId: "nav-donations" },
  { to: "/charity/fundraising", label: "Fundraising", icon: Megaphone, testId: "nav-fundraising" },
  { to: "/charity/programs", label: "Programs", icon: Target, testId: "nav-programs" },
  { to: "/charity/impact-report", label: "Impact Report", icon: FileText, testId: "nav-impact-report" },
  { to: "/charity/beneficiaries", label: "Beneficiaries", icon: UserSquare2, testId: "nav-beneficiaries" },
  {
    section: "Accounts", icon: Wallet, testId: "nav-section-accounts",
    items: [
      { to: "/charity/payments", label: "Online Payments", icon: CreditCard, testId: "nav-payments" },
      { to: "/charity/funds", label: "Funds", icon: Wallet, testId: "nav-funds" },
      { to: "/charity/finance", label: "Finance", icon: Banknote, testId: "nav-finance" },
      { to: "/charity/grants", label: "Grants", icon: HeartHandshake, testId: "nav-grants" },
      { to: "/charity/budget", label: "Budget vs Actual", icon: PieChart, testId: "nav-budget" },
      { to: "/charity/claims", label: "Gift Aid & 80G Claims", icon: Stamp, testId: "nav-claims" },
      { to: "/charity/expense-claims", label: "Expense Claims", icon: Receipt, testId: "nav-expense-claims" },
    ],
  },
  {
    section: "Governance", icon: Gavel, testId: "nav-section-governance",
    items: [
      { to: "/charity/governance", label: "Governance", icon: Gavel, testId: "nav-governance" },
      { to: "/governance/meetings", label: "Meetings & Resolutions", icon: CalendarDays,
        testId: "nav-meetings" },
      { to: "/governance/elections", label: "Elections", icon: Vote, testId: "nav-elections" },
      { to: "/governance/executive-committee", label: "Executive Committee", icon: Users2,
        testId: "nav-executive-committee" },
      { to: "/governance/vote", label: "Vote", icon: Vote, testId: "nav-vote" },
      { to: "/governance/inquiries", label: "Inquiries", icon: MessageSquareWarning, testId: "nav-inquiries" },
      { to: "/governance/conflicts", label: "Conflicts Register", icon: ShieldAlert, testId: "nav-conflicts" },
      { to: "/governance/approvals", label: "Approvals", icon: Stamp, testId: "nav-approvals" },
    ],
  },
  {
    section: "Regulatory", icon: ScrollText, testId: "nav-section-regulatory",
    items: [
      { to: "/compliance/risk", label: "Risk & AML", icon: Radar, testId: "nav-risk" },
      { to: "/regulatory/rules", label: "Rule Workbench", icon: ScrollText, testId: "nav-rules" },
      { to: "/regulatory/simulator", label: "Decision Simulator", icon: FlaskConical, testId: "nav-simulator" },
      { to: "/compliance/privacy", label: "Privacy Requests", icon: Scale, testId: "nav-privacy" },
    ],
  },
  {
    section: "Operations", icon: Truck, testId: "nav-section-operations",
    items: [
      { to: "/operations/supply", label: "Supply Chain", icon: Truck, testId: "nav-supply" },
      { to: "/operations/property", label: "Property", icon: Building, testId: "nav-property" },
      { to: "/operations/works", label: "Works Board", icon: KanbanSquare, testId: "nav-works" },
      { to: "/operations/apprenticeships", label: "Apprenticeships", icon: GraduationCap, testId: "nav-apprenticeships" },
      { to: "/operations/cafeteria", label: "Cafeteria", icon: UtensilsCrossed, testId: "nav-cafeteria",
        perm: ["cafeteria", "read"] },
      { to: "/me/leave", label: "My Leave", icon: CalendarDays, testId: "nav-my-leave" },
    ],
  },
  {
    section: "Admin", icon: Building2, testId: "nav-section-admin",
    items: [
      { to: "/settings/organization", label: "Organisation", icon: Building2, testId: "nav-organization" },
      { to: "/platform/audit", label: "Audit Explorer", icon: FileSearch, testId: "nav-audit" },
      { to: "/operations/hr", label: "HR & Payroll", icon: BadgeIndianRupee, testId: "nav-hr" },
      { to: "/operations/volunteers", label: "Volunteers", icon: HandHeart, testId: "nav-volunteers" },
      { to: "/operations/volunteer-hub", label: "Volunteer Hub", icon: HandHeart, testId: "nav-volunteer-hub" },
      { to: "/operations/time", label: "Time & Hours", icon: Clock, testId: "nav-time" },
      { to: "/operations/recognition", label: "Recognition", icon: Award, testId: "nav-recognition" },
      { to: "/regulatory/evidence", label: "Evidence Registry", icon: BookLock, testId: "nav-evidence" },
      { to: "/compliance/overview", label: "Compliance", icon: ShieldCheck, testId: "nav-compliance" },
      { to: "/settings/rbac", label: "Settings", icon: LayoutGrid, testId: "nav-rbac" },
    ],
  },
  { to: "/help", label: "Help & User Guide", icon: LifeBuoy, testId: "nav-help" },
  { to: "/settings/subscription", label: "Subscription", icon: BadgeCheck, testId: "nav-subscription",
    perm: ["licence", "read"] },
];

const FLAT = NAV.flatMap((i) => (i.section ? i.items : [i]));

const initials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase() || "U";

const ThemeControl = () => {
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();

  if (theme === "light") {
    return (
      <div className="flex items-center justify-between px-2.5 py-1" data-testid="theme-switch">
        <span className="text-[12.5px] font-medium text-muted-foreground">{t("Dark mode")}</span>
        <Switch checked={false} onCheckedChange={() => setTheme("dark")} aria-label={t("Enable dark mode")}
                data-testid="theme-dark-button" />
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 rounded-full bg-white/5 p-1" data-testid="theme-switch">
      {[["light", Sun, "Light"], ["dark", Moon, "Dark"]].map(([value, Icon, label]) => (
        <button key={value} type="button" onClick={() => setTheme(value)}
                data-testid={`theme-${value}-button`}
                className={cn(
                  "flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors duration-200",
                  theme === value ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground",
                )}>
          <Icon className="h-3 w-3" /> {t(label)}
        </button>
      ))}
    </div>
  );
};

const TextSizeControl = () => {
  const { textScale, setTextScale } = useTheme();
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1 rounded-full bg-white/5 p-1 ring-1 ring-border"
         data-testid="text-size-switch" title={t("Text size")}>
      {[[100, "text-[11px]"], [112, "text-[13px]"], [125, "text-[15px]"]].map(([value, cls]) => (
        <button key={value} type="button" onClick={() => setTextScale(value)}
                data-testid={`text-size-${value}`} aria-label={`${t("Text size")} ${value}%`}
                className={cn(
                  "flex flex-1 items-center justify-center rounded-full px-3 py-1 font-semibold leading-none transition-colors duration-200", cls,
                  textScale === value
                    ? "bg-primary text-primary-foreground shadow"
                    : "text-muted-foreground hover:text-foreground",
                )}>
          A
        </button>
      ))}
    </div>
  );
};

const LangControl = () => {
  const { i18n: inst } = useTranslation();
  return (
    <div className="flex items-center gap-1 rounded-full bg-white/5 p-1 ring-1 ring-border"
         data-testid="language-switch">
      {[["en", "English"], ["hi", "हिन्दी"]].map(([value, label]) => (
        <button key={value} type="button" onClick={() => setLanguage(value)}
                data-testid={`lang-${value}-button`}
                className={cn(
                  "flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors duration-200",
                  inst.language === value
                    ? "bg-primary text-primary-foreground shadow"
                    : "text-muted-foreground hover:text-foreground",
                )}>
          {label}
        </button>
      ))}
    </div>
  );
};

export const NavSection = ({ group, onNavigate }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const active = group.items.some((i) => location.pathname.startsWith(i.to));
  const [open, setOpen] = useState(active);

  return (
    <div className="pt-1.5">
      <button type="button" onClick={() => setOpen(!open)} data-testid={group.testId}
              className={cn("flex w-full items-center gap-2.5 rounded-[10px] px-3 py-[9px] text-[13px] font-semibold transition-colors duration-200",
                active ? "text-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground")}>
        <group.icon className="h-4 w-4 shrink-0" />
        <span className="flex-1 truncate text-left">{t(group.section)}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform duration-200",
          open ? "rotate-0" : "-rotate-90")} />
      </button>
      {open && (
        <div className="ml-3 space-y-0.5 border-l border-border pl-2">
          {group.items.map((item) => (
            <NavLink key={item.to} to={item.to} data-testid={item.testId} onClick={onNavigate}
                     className={({ isActive }) => cn(
                       "group flex items-center gap-2.5 rounded-[10px] px-3 py-[7px] text-[12.5px] transition-colors duration-200",
                       isActive
                         ? "bg-accent font-semibold text-accent-foreground dark:bg-primary dark:text-primary-foreground"
                         : "font-medium text-muted-foreground hover:bg-muted hover:text-foreground")}>
              <item.icon className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 truncate">{t(item.label)}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

export const AppShell = ({ children, licenceBlocked = false }) => {
  const { user, logout, can } = useAuth();
  const { t, i18n: inst } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [impersonation, setImpersonation] = useState(null);
  const [dataHindi, setDataHindi] = useState(
    () => localStorage.getItem("servecor_translate_data") === "1");

  useEffect(() => {
    if (inst.language === "hi") startDomTranslation(dataHindi, (p, b) => api.post(p, b));
    else stopDomTranslation();
  }, [inst.language, dataHindi]);

  useEffect(() => {
    api.put("/i18n/language", { language: inst.language }).catch(() => {});
  }, [inst.language]);

  const toggleDataHindi = (v) => {
    setDataHindi(v);
    localStorage.setItem("servecor_translate_data", v ? "1" : "0");
    setDataTranslation(v);
  };

  useEffect(() => {
    import("@/lib/api").then(({ api }) =>
      api.get("/licence/impersonation-banner")
        .then((r) => setImpersonation(r.data?.active ? r.data : null))
        .catch(() => {}));
  }, []);
  const location = useLocation();
  const navigate = useNavigate();

  const nav = useMemo(() => {
    const allowed = (i) => !i.hidden && (!i.perm || can(i.perm[0], i.perm[1]));
    if (licenceBlocked) return FLAT.filter((i) => i.to === "/settings/subscription" && allowed(i));
    return NAV.map((i) => (i.section ? { ...i, items: i.items.filter(allowed) } : i))
      .filter((i) => (i.section ? i.items.length > 0 : allowed(i)));
  }, [can, licenceBlocked]);   // eslint-disable-line react-hooks/exhaustive-deps

  const crumb = FLAT.find((i) => location.pathname.startsWith(i.to));
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return FLAT.filter((i) => i.label.toLowerCase().includes(q)
      || t(i.label).toLowerCase().includes(q)).slice(0, 6);
  }, [query, t]);

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[248px_1fr]">
      <aside
        className={cn(
            "fixed left-0 top-0 z-50 flex w-[248px] flex-col border-r border-border bg-[hsl(var(--sidebar))] backdrop-blur-2xl transition-transform duration-300 dark:bg-[hsl(var(--sidebar)/0.55)] lg:sticky lg:top-0 lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full",
        )}
        style={{ height: "var(--vh-zoom, 100vh)" }}
      >
        <div className="flex h-[74px] shrink-0 items-center justify-between px-4">
          <Link to="/dashboard/executive" className="group flex items-center gap-2.5" data-testid="brand-link">
            <img src="/brand/servecor-mark.png" alt="Servecor" data-testid="sidebar-logo"
                 className="h-14 w-auto rounded-xl bg-[#E2E8F0] p-1 transition-transform duration-300 group-hover:scale-105" />
            <span>
              <span className="block font-display text-[19px] font-bold leading-tight tracking-tight text-foreground">
                ServeCOR
              </span>
              <span className="block text-[9px] tracking-wide text-muted-foreground">
                {t("Empowering Trust. Enabling Impact")}
              </span>
            </span>
          </Link>
          <button className="lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-close">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto px-2.5 pb-3">
          {nav.map((item) => (item.section ? (
            <NavSection key={item.section} group={item} onNavigate={() => setOpen(false)} />
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                cn(
                  "group flex items-center gap-2.5 rounded-[10px] px-3 py-[9px] text-[13px] transition-colors duration-200",
                  isActive
                    ? "bg-accent font-semibold text-accent-foreground dark:bg-primary dark:text-primary-foreground dark:shadow-[0_6px_16px_-8px_hsl(var(--primary))]"
                    : "font-medium text-muted-foreground hover:bg-muted hover:text-foreground",
                )
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span className="flex-1 truncate">{t(item.label)}</span>
              {item.chevron && <ChevronDown className="h-3 w-3 opacity-50" />}
            </NavLink>
          )))}
        </nav>

        <div className="shrink-0 space-y-2.5 px-3 pb-4">
          <LangControl />
          {inst.language === "hi" && (
            <div className="flex items-center justify-between rounded-full bg-white/5 px-3.5 py-1.5 ring-1 ring-border"
                 data-testid="data-translate-control">
              <span className="text-[11.5px] font-medium text-muted-foreground">डेटा भी हिन्दी में</span>
              <Switch checked={dataHindi} onCheckedChange={toggleDataHindi}
                      data-testid="data-translate-switch" />
            </div>
          )}
          <ThemeControl />
          <TextSizeControl />
          <div className="flex items-center gap-2.5 rounded-xl border border-border bg-card p-2.5" data-testid="current-user">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/12 text-[11px] font-bold text-primary">
              {initials(user?.name)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12.5px] font-semibold text-foreground">{user?.name}</div>
              <div className="truncate text-[11px] text-muted-foreground">{user?.role_label || user?.role}</div>
            </div>
            <button onClick={logout} data-testid="logout-button" title={t("Sign out")}
                    className="rounded-lg p-1.5 text-muted-foreground transition-colors duration-150 hover:bg-destructive/10 hover:text-destructive">
              <LogOut className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex min-h-screen flex-col">
        {impersonation && (
          <div data-testid="impersonation-banner"
               className="flex items-center justify-center gap-2 bg-amber-500/95 px-4 py-1.5 text-[12.5px] font-semibold text-amber-950">
            <ShieldAlert className="h-3.5 w-3.5" />
            {t("ServeCOR support is currently viewing this tenant")}
            {impersonation.tier ? ` (${impersonation.tier} ${t("access")})` : ""} — {t("every action is being recorded.")}
          </div>
        )}
        <header className="sticky top-0 z-30 flex h-[62px] items-center gap-3 border-b border-border bg-[hsl(var(--sidebar))]/95 px-4 backdrop-blur-xl dark:bg-[hsl(var(--sidebar)/0.6)] md:px-6">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open">
            <Menu className="h-5 w-5 text-muted-foreground" />
          </button>

          <div className="relative w-full max-w-[360px]">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && results[0]) { setQuery(""); navigate(results[0].to); }
                if (e.key === "Escape") setQuery("");
              }}
              placeholder={t("Search across ServeCOR…")}
              data-testid="global-search-input"
              className="h-9 w-full rounded-full bg-muted/70 pl-9 pr-14 text-[12.5px] text-foreground outline-none ring-1 ring-transparent transition-all duration-200 placeholder:text-muted-foreground focus:bg-card focus:ring-primary/40"
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
              ⌘K
            </span>
            {results.length > 0 && (
              <div className="panel absolute left-0 top-11 z-40 w-full overflow-hidden p-1" data-testid="global-search-results">
                {results.map((r) => (
                  <button key={r.to} onClick={() => { setQuery(""); navigate(r.to); }}
                          data-testid={`search-result-${r.testId}`}
                          className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[12.5px] text-foreground transition-colors duration-150 hover:bg-muted">
                    <r.icon className="h-3.5 w-3.5 text-primary" />
                    <span className="flex-1 truncate">{t(r.label)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="ml-auto flex items-center gap-1.5">
            <div className="mr-1 hidden items-center gap-1 text-[11.5px] text-muted-foreground xl:flex">
              <span>{crumb ? t(crumb.label) : t("Not found")}</span>
              <ChevronRight className="h-3 w-3" />
              <span className="font-medium text-foreground">{t("Live")}</span>
            </div>
            <NotificationBell />

            <button data-testid="topbar-mail"
                    className="hidden rounded-lg p-2 text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground sm:block">
              <Mail className="h-4 w-4" />
            </button>
            <button data-testid="topbar-locale" title={t("Language")}
                    onClick={() => setLanguage(inst.language === "hi" ? "en" : "hi")}
                    className="hidden rounded-lg p-2 text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground sm:block">
              <Globe className="h-4 w-4" />
            </button>
            <button data-testid="tenant-chip"
                    className="flex items-center gap-2 rounded-xl border border-border bg-card px-2.5 py-1.5 transition-colors duration-150 hover:border-primary/40">
              <span className="grid h-6 w-6 place-items-center rounded-md bg-gradient-to-br from-primary to-[hsl(var(--teal))] text-[10px] font-bold text-white">
                SD
              </span>
              <span className="hidden text-[12.5px] font-medium text-foreground sm:block">ServeCOR Demo</span>
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </button>
          </div>
        </header>

        <main key={inst.language} className="flex-1 p-4 md:p-6 lg:px-7 lg:py-6">{children}</main>
      </div>
      <GuidedTour />
    </div>
  );
};
